import * as React from "react";

// MUI
import Button from "@mui/material/Button";
import { Grid2 } from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

// components
import SeriesDropDown from "../adTesting/seriesDropDown";
import AdSizeDropDown from "../adTesting/adSizeDropDown";
import RegionDropDown from "../adTesting/regionDropDown";
import CampaignDropDown from "../adTesting/campaignDropDown";
import EmptyDropDown from "../adTesting/emptyDropDown";
import EnvDropDown from "../adTesting/envDropDown";
import IndependentDataCheck from "../adTesting/independentDataCheck";

// Menu props
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250
		}
	}
};

// --- Data loaders ---
async function getSeriesAPI() {
	const response = await fetch(
		"https://d2x0ayeq55tlvj.cloudfront.net/seriesapi/global/seriesapi.json"
	);
	const data = await response.json();
	// Add No Series option
	data.push({
		seriesname: "No Series",
		seriescode: "noseries",
		ToyotaAPIName: "noseries",
		modelyear: "",
		cg_active: false,
		group: []
	});
	data.sort((a, b) => a.seriesname.localeCompare(b.seriesname));
	return data;
}

async function getAdData() {
	const cacheBuster = `cb=${Date.now()}`;
	const response = await fetch(
		`https://tcaadev.com/FT_Testing/src/testing_catalog.json?${cacheBuster}`
	);
	if (!response.ok) throw new Error("Failed to load testing_catalog.json");
	const data = await response.json();
	return data;
}

export default function AdTestingComponent() {
	const [seriesData, setSeriesData] = React.useState(null);
	const [adData, setAdData] = React.useState([]);

	const [focusSeries, setFocusSeries] = React.useState(null);
	const [focusAdSizes, setFocusAdSizes] = React.useState([]);
	const [focusRegion, setFocusRegion] = React.useState("");
	const [focusCampaign, setFocusCampaign] = React.useState(null);
	const [focusEnv, setFocusEnv] = React.useState(null);
	const [layout, setLayout] = React.useState("vertical");

	React.useEffect(() => {
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Keep controls in view if desired (uses absolute positioning when needed)
	React.useEffect(() => {
		function handleScroll() {
			const allAdControls = document.getElementById("allAdControls");
			if (!allAdControls) return;
			const rect = allAdControls.getBoundingClientRect();
			const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
			if (!isVisible) {
				// Ensure absolute positioning before moving
				const computed = window.getComputedStyle(allAdControls);
				if (computed.position === "static") {
					//allAdControls.style.position = "absolute";
				}
				allAdControls.style.top = `${window.scrollY + 10}px`;
			}
		}
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// If all QS-driven states are ready, auto-submit
	React.useEffect(() => {
		querystringVarsSetup();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusAdSizes, focusRegion, focusCampaign, focusEnv, focusSeries]);

	async function fetchData() {
		try {
			const [series, ads] = await Promise.all([getSeriesAPI(), getAdData()]);
			setSeriesData(series);
			setAdData(ads);

			// Hydrate defaults from query string
			const urlParams = new URLSearchParams(window.location.search);
			const seriesQS = urlParams.get("series");
			const adSizes = urlParams.get("adSizes");
			const region = urlParams.get("region");
			const campaign = urlParams.get("campaign");
			const env = urlParams.get("env");

			if (seriesQS) setFocusSeries(seriesQS);
			if (adSizes) setFocusAdSizes(adSizes.split(","));
			if (region) setFocusRegion(region);
			if (campaign) setFocusCampaign(campaign);
			if (env) setFocusEnv(env);
		} catch (err) {
			console.error("Failed to fetch initial data", err);
		}
	}

	function querystringVarsSetup() {
		if (
			focusSeries &&
			focusAdSizes.length > 0 &&
			focusRegion &&
			focusCampaign &&
			focusEnv
		) {
			SubmitForAd(
				focusSeries,
				focusAdSizes,
				focusRegion,
				focusCampaign,
				focusEnv
			);
		}
	}

	function SubmitForAd(
		focusSeries,
		focusAdSizes,
		focusRegion,
		focusCampaign,
		focusEnv
	) {
		document.getElementById("errorSubmit").innerText = "";

		const adObjects = getAdObject(
			focusSeries,
			focusAdSizes,
			focusRegion,
			focusCampaign,
			focusEnv
		);
		buildAds(adObjects);

		const queryString = `?series=${focusSeries}&adSizes=${focusAdSizes.join(
			","
		)}&region=${focusRegion}&campaign=${focusCampaign}&env=${focusEnv}&mode=adtesting`;
		window.history.pushState({}, "", queryString);
	}

	function buildAds(adObjects) {
		const allAdsDiv = document.getElementById("allAds");
		if (!allAdsDiv) return;
		allAdsDiv.innerHTML = ""; // Clear previous ads

		let numPLI_Counter = 0;
		adObjects.forEach((obj) => {
			const adDiv = document.createElement("div");
			adDiv.id = "ad" + numPLI_Counter; // unique ID
			adDiv.className = "adDiv";
			allAdsDiv.appendChild(adDiv);

			// Meta header
			const metaHolder = document.createElement("div");
			metaHolder.className = "metaHolder";

			const adSize = document.createElement("div");
			adSize.innerText = obj.adSize + " -";
			adSize.className = "adSizeHolder";

			const adPli = document.createElement("div");
			adPli.innerText = " - " + obj.pli;
			adPli.className = "adPli";

			const adLinks = document.createElement("div");
			const baseURL = "https://creativepreview.flashtalking.net/pv/";
			let adLinksStr = "";
			if (obj.livelink && obj.livelink.length > 0) {
				adLinksStr += `<a href="${baseURL}${obj.livelink}" target="_blank" rel="noreferrer noopener">View All</a>`;
			}
			if (obj.staginglink && obj.staginglink.length > 0) {
				adLinksStr += `<a href="${baseURL}${obj.staginglink}" target="_blank" rel="noreferrer noopener">Staging</a>`;
			}
			adLinks.innerHTML = adLinksStr;
			adLinks.className = "adLinksHolder";

			metaHolder.appendChild(adSize);
			metaHolder.appendChild(adPli);
			metaHolder.appendChild(adLinks);
			adDiv.appendChild(metaHolder);

			// Lookup Toyota API Name from selected series
			let toyotaAPIName = "";
			if (Array.isArray(seriesData) && focusSeries) {
				const match = seriesData.find((s) => s.seriesname === focusSeries);
				if (match) toyotaAPIName = match.ToyotaAPIName || "";
			}

			const variables = {
				pli: obj.pli,
				width: obj.width,
				height: obj.height,
				series: toyotaAPIName,
				adDiv: adDiv,
				env: focusEnv
			};
			console.log("++++++++ " + focusCampaign, focusRegion);
			if (focusCampaign.includes("tcuv") && focusRegion === "ne") {
				console.log("++++++++ " + focusCampaign?.includes("tcuv"), focusRegion);
				loadAdTagHtmlWithVariables(
					"https://tcaadev.com/FT_Testing/src/buildTagVIN.html",
					variables,
					adDiv
				);
			} else {
				if (
					(focusCampaign.includes("ongoing") &&
						focusCampaign.includes("quarterly") &&
						focusRegion === "ne") ||
					focusRegion === "phx" ||
					focusRegion === "cat"
				) {
					loadAdTagHtmlWithVariables(
						"https://tcaadev.com/FT_Testing/src/buildTagIns.html",
						variables,
						adDiv
					);
				} else {
					loadAdTagHtmlWithVariables(
						"https://tcaadev.com/FT_Testing/src/buildTag.html",
						variables,
						adDiv
					);
				}
			}

			numPLI_Counter++;

			if (numPLI_Counter === 1) {
				setTimeout(() => {
					const iframe = adDiv.querySelector("iframe");
					if (iframe) {
						try {
							const iframeNamesJSON = JSON.parse(iframe.name || "{}");
							let adTypeNotice = document.getElementById("adTypeNotice");
							if (!adTypeNotice) {
								adTypeNotice = document.createElement("div");
								adTypeNotice.id = "adTypeNotice";
							}
							// Insert at top of adDiv
							adDiv.insertBefore(adTypeNotice, adDiv.firstChild);

							const cname = (iframeNamesJSON.creativeName || "").toLowerCase();
							console.log("cname", cname);
							if (cname.includes("static") || cname.includes("animated")) {
								adTypeNotice.innerHTML = `default ads are showing`;
							} else if (cname.includes("no_series")) {
								adTypeNotice.innerHTML = `No Series ads are showing`;
							} else if (cname) {
								// DCO ad
								adTypeNotice.innerHTML = ``; // clear notice
							}
						} catch (e) {
							// ignore malformed iframe.name
						}
					}

					// Position the feedsOfferHolder near the first ad if present
					const feeds = document.getElementById("feedsOfferHolder");
					if (feeds) {
						feeds.style.top = adDiv.offsetTop + "px";
						feeds.style.left = adDiv.offsetLeft + adDiv.offsetWidth + 20 + "px";
					}
				}, 3000);
			}
		});
	}

	async function loadAdTagHtmlWithVariables(filePath, variables, adDiv) {
		try {
			const response = await fetch(filePath);
			if (!response.ok) {
				throw new Error(`Failed to fetch ${filePath}: ${response.statusText}`);
			}
			let htmlContent = await response.text();

			for (const [key, value] of Object.entries(variables)) {
				const placeholder = `{{${key}}}`;
				htmlContent = htmlContent.replace(
					new RegExp(placeholder, "g"),
					String(value)
				);
			}

			adDiv.innerHTML += htmlContent;

			// Wait up to 5s for an <ins> to appear, then inject the insert script once
			let waited = 0;
			const interval = 100;
			const max = 5000;
			const waitForInsTag = setInterval(() => {
				const ins = adDiv.querySelector("ins");
				if (ins) {
					if (!ins.querySelector("script[src*='ftInsert.js']")) {
						const script = document.createElement("script");
						script.src = `https://cdn.flashtalking.com/armada/tags/placement-ft-inserts/${variables.pli}-ftInsert.js`;
						ins.appendChild(script);
					}
					clearInterval(waitForInsTag);
				} else if ((waited += interval) >= max) {
					clearInterval(waitForInsTag);
				}
			}, interval);
		} catch (error) {
			console.error("Error loading HTML file:", error);
		}
	}

	function getAdObject(
		focusSeries,
		focusAdSizes,
		focusRegion,
		focusCampaign,
		focusEnv
	) {
		const adObj = [];
		for (const ad of adData) {
			if (
				ad.region === focusRegion &&
				ad.campaign === focusCampaign &&
				focusAdSizes.includes(`${ad.width}x${ad.height}`) &&
				focusEnv === ad.environment
			) {
				adObj.push({
					pli: ad.placement,
					width: ad.width,
					height: ad.height,
					adSize: `${ad.width}x${ad.height}`,
					env: ad.environment,
					livelink: ad.live_link,
					staginglink: ad.staging_link
				});
			}
		}
		return adObj;
	}

	function onClickSubmit() {
		const ready =
			!!focusSeries &&
			focusAdSizes.length !== 0 &&
			!!focusRegion &&
			!!focusCampaign &&
			!!focusEnv;

		if (ready) {
			SubmitForAd(
				focusSeries,
				focusAdSizes,
				focusRegion,
				focusCampaign,
				focusEnv
			);
		} else {
			document.getElementById("errorSubmit").innerText =
				"Please populate all dropdowns";
		}
	}

	const getFocusSeries = (data) => setFocusSeries(data);
	const getFocusAdSizes = (data) => setFocusAdSizes(data);
	const getFocusEnv = (data) => {
		setFocusEnv(data);
		setFocusAdSizes([]);
	};
	const getFocusRegion = (data) => {
		setFocusRegion(data);
		setFocusCampaign(null);
		setFocusEnv(null);
		setFocusAdSizes([]);
	};
	const getFocusCampaign = (data) => {
		setFocusCampaign(data);
		setFocusEnv("");
		setFocusAdSizes([]);
	};

	return (
		<div className="App">
			<h1>Build Test Ads</h1>
			<Grid2 container spacing={5}>
				<Grid2 xs={2} id="allAdControls">
					{adData ? (
						<RegionDropDown
							adData={adData}
							getFocusRegion={getFocusRegion}
							theFocusRegion={focusRegion}
						/>
					) : (
						<p>Loading...</p>
					)}

					{adData && focusRegion ? (
						<CampaignDropDown
							adData={adData}
							getFocusCampaign={getFocusCampaign}
							theFocusCampaign={focusCampaign}
							theFocusRegion={focusRegion}
						/>
					) : (
						<EmptyDropDown emptyLabel="Campaign" />
					)}

					{adData && focusRegion && focusCampaign ? (
						<EnvDropDown
							adData={adData}
							getFocusEnv={getFocusEnv}
							theFocusEnv={focusEnv}
							theFocusRegion={focusRegion}
							theFocusCampaign={focusCampaign}
						/>
					) : (
						<EmptyDropDown emptyLabel="Enviroments" />
					)}

					{adData && focusRegion && focusCampaign ? (
						<AdSizeDropDown
							adData={adData}
							getAdFocusSizes={getFocusAdSizes}
							theFocusAdSize={focusAdSizes}
							theFocusRegion={focusRegion}
							theFocusEnv={focusEnv}
							theFocusCampaign={focusCampaign}
						/>
					) : (
						<EmptyDropDown emptyLabel="Ad Sizes" />
					)}

					{seriesData ? (
						<SeriesDropDown
							seriesAPIData={seriesData}
							getFocusSeries={getFocusSeries}
							theFocusSeries={focusSeries}
							theFocusRegion={focusRegion}
							theFocusCampaign={focusCampaign}
						/>
					) : (
						<p>Loading...</p>
					)}

					<FormControl sx={{ m: 1, width: 300 }}>
						<InputLabel id="layout-select-label">Layout</InputLabel>
						<Select
							labelId="layout-select-label"
							id="layout-select"
							value={layout}
							MenuProps={MenuProps}
							onChange={(e) => {
								setLayout(e.target.value);
								const allAdsDiv = document.getElementById("allAds");
								if (!allAdsDiv) return;
								if (e.target.value === "horizontal") {
									allAdsDiv.style.display = "flex";
									allAdsDiv.style.flexDirection = "row";
									allAdsDiv.style.flexWrap = "wrap";
								} else {
									allAdsDiv.style.display = "flex";
									allAdsDiv.style.flexDirection = "column";
									allAdsDiv.style.flexWrap = "nowrap";
								}
							}}
							input={<OutlinedInput label="Layout" />}
						>
							<MenuItem value="horizontal">Horizontal Layout</MenuItem>
							<MenuItem value="vertical">Vertical Layout</MenuItem>
						</Select>
					</FormControl>

					{/* <Button variant="outlined" onClick={onClickSubmit}>SUBMIT</Button> */}
					<div id="errorSubmit"></div>
				</Grid2>

				<Grid2 xs={10}>
					<div
						id="allAds"
						style={{
							display: "flex",
							flexWrap: layout === "horizontal" ? "wrap" : "nowrap",
							flexDirection: layout === "horizontal" ? "row" : "column"
						}}
					></div>
				</Grid2>
			</Grid2>

			{seriesData &&
			focusSeries &&
			focusAdSizes &&
			focusRegion &&
			focusCampaign &&
			focusEnv ? (
				<IndependentDataCheck
					seriesAPIData={seriesData}
					theFocusSeries={focusSeries}
					theFocusRegion={focusRegion}
				/>
			) : null}
		</div>
	);
}
